// @mui
import Fab from '@mui/material/Fab';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
// routes
import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';
// utils
import { fCurrency } from 'src/utils/format-number';
// redux
import { useDispatch } from 'src/redux/store';
import { addToCart } from 'src/redux/slices/product';
// types
// import { IProduct } from 'src/types/product';
// components
import Label from 'src/components/label';
import Image from 'src/components/image';
import Iconify from 'src/components/iconify';
import { useSnackbar } from 'src/components/snackbar';
import { Typography } from '@mui/material';
import { useResponsive } from 'src/hooks/use-responsive';
import { startSales } from 'src/constants';

// ----------------------------------------------------------------------

type Props = {
  product: any;
  isRTL?: boolean;
};

export default function ProductItem({ product, isRTL }: Props) {
  const {
    id,
    _id,
    barcode,
    itemType,
    name,
    nameAr,
    desc,
    descAr,
    desc2,
    desc2Ar,
    categoryId,
    categoryName,
    categoryNameAr,
    coverUrl,
    thumb,
    photo,
    photoAr,
    price,
    colors,
    available,
    cost,
    size,
    weight,
    unit,
    calories,
    fat,
    sodium,
    sugar,
    newLabel,
    saleLabel,
  } = product;
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const mdUp = useResponsive('up', 'md');
  const linkTo = paths.product.details(id);

  const handleAddCart = async () => {
    const newProduct = {
      id,
      _id,
      itemId: _id, // itemId from _id
      barcode,
      itemType,
      name,
      nameAr,
      desc,
      descAr,
      desc2,
      desc2Ar,
      categoryId,
      categoryName,
      categoryNameAr,
      coverUrl,
      photo,
      photoAr,
      available,
      qty: 1, // Make qty same as quantity
      cost,
      totalCost: cost ? cost * 1 : undefined, // cost * quantity
      price,
      totalPrice: price * 1, // price * quantity
      discount: 0, // default discount
      amount: price * 1, // final amount after discount
      colors: [colors[0]],
      size,
      weight,
      unit,
      calories,
      fat,
      sodium,
      sugar,
      quantity: 1,
      subTotal: price, // Initialize subTotal as price * quantity (1)
    };
    try {
      dispatch(addToCart(newProduct));

      // Show success toast
      enqueueSnackbar(isRTL ? `تم إضافة ${nameAr || name} إلى السلة` : `${name} added to cart`, {
        variant: 'success',
      });
    } catch (error) {
      console.error(error);

      // Show error toast
      enqueueSnackbar(isRTL ? 'فشل في إضافة العنصر إلى السلة' : 'Failed to add item to cart', {
        variant: 'error',
      });
    }
  };

  const renderLabels = (newLabel?.enabled || saleLabel?.enabled) && (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{ position: 'absolute', zIndex: 9, top: 16, right: 16 }}
    >
      {newLabel?.enabled && (
        <Label variant="filled" color="info">
          {newLabel.content}
        </Label>
      )}
      {saleLabel?.enabled && (
        <Label variant="filled" color="error">
          {saleLabel.content}
        </Label>
      )}
    </Stack>
  );

  const renderImg = (
    <Box sx={{ position: 'relative', p: 1 }}>
      {startSales && (
        <Fab
          color="warning"
          size="medium"
          className="add-cart-btn"
          onClick={handleAddCart}
          sx={{
            right: 16,
            top: { xs: 16, md: 'auto' },
            bottom: { xs: 'auto', md: 16 },
            zIndex: 9,
            opacity: { xs: 1, md: 0 }, // Always visible on mobile, hidden on desktop until hover
            position: 'absolute',
            transition: (theme) =>
              theme.transitions.create('all', {
                easing: theme.transitions.easing.easeInOut,
                duration: theme.transitions.duration.shorter,
              }),
          }}
        >
          <Iconify icon="solar:cart-plus-bold" width={24} />
        </Fab>
      )}

      <Link component={RouterLink} href={linkTo} color="inherit" variant="subtitle2" noWrap>
        <Image alt={name} src={thumb} sx={{ borderRadius: 1.5 }} />
      </Link>
    </Box>
  );

  const renderContent = (
    <Stack spacing={2} sx={{ p: 3, pt: 2 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ position: 'relative' }}
      >
        <Link component={RouterLink} href={linkTo} color="inherit">
          <Typography variant="subtitle2" noWrap sx={{ ml: mdUp ? 0 : -2 }}>
            {isRTL ? nameAr : name}
          </Typography>
        </Link>
        <Box
          sx={{
            position: 'absolute',
            bottom: 40,
            left: -11,
            display: 'flex',
            minWidth: 70,
            alignItems: 'center',
            justifyContent: 'center',
            color: 'text.secondary',
            backgroundColor: '#118D57',
            px: 1,
            py: 0.5,
            borderRadius: 1,
          }}
        >
          <Typography sx={{ fontWeight: 'bold', color: '#fff' }}>{fCurrency(price)}</Typography>
          {size && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 90,
                minWidth: 70,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.secondary',
                backgroundColor: '#7A0916aa',
                px: 1,
                py: 0.5,
                borderRadius: 1,
              }}
            >
              <Typography sx={{ fontWeight: 'bold', color: '#fff' }}>{size}</Typography>
            </Box>
          )}
          {calories && calories > 0 && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: size ? 180 : 90,
                minWidth: 70,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.secondary',
                backgroundColor: '#FF6B35aa',
                px: 0.5,
                py: 0.5,
                borderRadius: 1,
              }}
            >
              <Typography sx={{ fontWeight: 'bold', color: '#fff' }}>{calories} cal</Typography>
            </Box>
          )}
        </Box>
      </Stack>
    </Stack>
  );

  return (
    <Card
      sx={{
        '&:hover .add-cart-btn': {
          opacity: 1,
        },
      }}
    >
      {renderLabels}

      {renderImg}

      {renderContent}
    </Card>
  );
}
